import React, { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional fallback UI. Defaults to null (invisible recovery). */
  fallback?: ReactNode;
  /** Optional label for dev-only error logging */
  label?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Lightweight error boundary for critical UI sections.
 * Catches render errors and displays fallback (or nothing) instead of crashing the page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
